import { Component, inject, signal, OnInit } from '@angular/core';
import { KeyValuePipe } from '@angular/common';
import { AdminApiService } from './admin-api.service';
import { DevUserService, DevUser, isAdminSuppressed } from './dev-user.service';
import { SessionContextService } from '../../core/services/session-context.service';

type PickerStep = 'closed' | 'companies' | 'users';

const USER_TYPE_MAP: Record<string | number, string> = {
  1: 'Individual', 2: 'Group',
  'i': 'Individual', 'I': 'Individual',
  'g': 'Group', 'G': 'Group',
};

const USER_GROUP_MAP: Record<number, string> = {
  0: 'Back Office',
  1: 'Rep',
  2: 'Other',
};

@Component({
  selector: 'app-dev-user-picker',
  standalone: true,
  imports: [KeyValuePipe],
  template: `
    <div class="dev-picker">
      <div class="dev-picker-label">DEV</div>

      <!-- Current user display + controls -->
      <div class="dev-picker-current" (click)="openDropdown()">
        <span class="dev-picker-name">{{ devUser.displayName() }}</span>
        @if (devUser.isLoading()) {
          <span class="dev-picker-loading">loading...</span>
        }
        <span class="dev-picker-arrow">{{ step() !== 'closed' ? '\u25B2' : '\u25BC' }}</span>
      </div>

      @if (devUser.currentUser()) {
        <button class="dev-picker-clear" (click)="clearSelection($event)" title="Clear user selection">\u00D7</button>
      }

      <!-- Dropdown panel -->
      @if (step() !== 'closed') {
        <div class="dev-picker-dropdown" (click)="$event.stopPropagation()">

          <!-- Step 1: Company search -->
          @if (step() === 'companies') {
            <div class="dp-header">
              <span class="dp-step-label">1. Select Company</span>
            </div>
            <input
              type="text"
              class="dev-picker-search"
              placeholder="Search companies..."
              (input)="onCompanySearch($event)"
              autofocus
            />
            <div class="dev-picker-results">
              @for (co of companyResults(); track co.id) {
                <div class="dev-picker-item company-item" (click)="selectCompany(co)">
                  <span class="dev-item-name">{{ co.name }}</span>
                  <span class="dev-item-meta">
                    ID:{{ co.id }}
                    @if (co.user_type_id) {
                      <span class="dev-badge type">{{ userTypeLabel(co.user_type_id) }}</span>
                    }
                  </span>
                  <span class="dev-picker-arrow">\u25B6</span>
                </div>
              }
              @if (companyResults().length === 0 && companySearchTerm().length >= 2) {
                <div class="dev-picker-empty">No companies found</div>
              }
              @if (companySearchTerm().length < 2) {
                <div class="dev-picker-empty">Type 2+ characters to search companies...</div>
              }
            </div>
          }

          <!-- Step 2: Users in selected company -->
          @if (step() === 'users') {
            <div class="dp-header">
              <button class="dp-back" (click)="backToCompanies()">\u2190</button>
              <span class="dp-step-label">{{ selectedCompanyName() }}</span>
              <span class="dp-count">{{ companyUsers().length }} users</span>
            </div>
            <input
              type="text"
              class="dev-picker-search"
              placeholder="Filter users..."
              (input)="onUserFilter($event)"
              autofocus
            />
            @if (usersLoading()) {
              <div class="dev-picker-empty">Loading users...</div>
            }
            <div class="dev-picker-results">
              @for (user of filteredUsers(); track user.id) {
                <div
                  class="dev-picker-item user-item"
                  [class.selected]="devUser.currentUser()?.id === user.id"
                  (click)="pickUser(user)"
                >
                  <span class="dev-item-name">{{ user.first_name }} {{ user.last_name }}</span>
                  <span class="dev-item-meta">
                    {{ user.username }}
                    <span class="dev-badge type">{{ userTypeLabel(user.user_type_id) }}</span>
                    <span class="dev-badge group">{{ userGroupLabel(user.user_group) }}</span>
                    @if (user.admin === 1) { <span class="dev-badge admin">admin</span> }
                    @if (user.suppress_admin) { <span class="dev-badge suppress">suppressed</span> }
                  </span>
                </div>
              }
              @if (!usersLoading() && filteredUsers().length === 0) {
                <div class="dev-picker-empty">No users match filter</div>
              }
            </div>
          }
        </div>
      }

      <!-- Context tags when user is selected -->
      @if (devUser.currentContext(); as ctx) {
        <div class="dev-picker-context">
          @for (entry of devUser.appAccessMap() | keyvalue; track entry.key) {
            <span class="dev-ctx-tag">{{ entry.key }}: {{ entry.value }}</span>
          }
          <span class="dev-ctx-perms">{{ devUser.permissionCodes().length }} permissions</span>
        </div>
      }

      <!-- Shelf access status -->
      @if (sessionCtx.hasSession() && hasShelfAccess()) {
        <div class="dev-shelf-status">
          @for (app of shelfAppTypes; track app) {
            <span class="dev-shelf-app">{{ app }}:<span [class]="'dev-shelf-' + sessionCtx.shelfAccessFor(app)">{{ shelfLabel(sessionCtx.shelfAccessFor(app)) }}</span></span>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    .dev-picker {
      display: flex;
      align-items: center;
      gap: 8px;
      background: #1a1a2e;
      padding: 6px 16px;
      font-family: 'Roboto Mono', monospace;
      font-size: 11px;
      position: relative;
      flex-wrap: wrap;
    }
    .dev-picker-label {
      background: #e74c3c;
      color: #fff;
      padding: 2px 8px;
      border-radius: 3px;
      font-weight: 700;
      font-size: 10px;
      letter-spacing: 1px;
    }
    .dev-picker-current {
      color: #fff;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .dev-picker-name { color: #4fc3f7; }
    .dev-picker-loading { color: #ffd54f; font-style: italic; }
    .dev-picker-arrow { color: #888; font-size: 9px; }

    .dev-picker-clear {
      background: none;
      border: 1px solid #555;
      color: #e74c3c;
      font-size: 14px;
      line-height: 1;
      padding: 1px 6px;
      border-radius: 3px;
      cursor: pointer;
      font-weight: 700;
    }
    .dev-picker-clear:hover { background: #e74c3c; color: #fff; }

    .dev-picker-dropdown {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      min-width: 560px;
      background: #1a1a2e;
      border: 1px solid #333;
      border-radius: 0 0 6px 6px;
      z-index: 9999;
      box-shadow: 0 8px 24px rgba(0,0,0,0.5);
    }

    .dp-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 12px;
      border-bottom: 1px solid #333;
      background: #12122a;
    }
    .dp-step-label { color: #4fc3f7; font-weight: 600; font-size: 12px; }
    .dp-count { color: #888; font-size: 10px; margin-left: auto; }
    .dp-back {
      background: none;
      border: 1px solid #444;
      color: #4fc3f7;
      padding: 2px 8px;
      border-radius: 3px;
      cursor: pointer;
      font-size: 12px;
    }
    .dp-back:hover { background: #2a2a4a; }

    .dev-picker-search {
      width: 100%;
      padding: 8px 12px;
      background: #0d0d1a;
      border: none;
      border-bottom: 1px solid #333;
      color: #fff;
      font-family: 'Roboto Mono', monospace;
      font-size: 12px;
      outline: none;
      box-sizing: border-box;
    }
    .dev-picker-search::placeholder { color: #666; }
    .dev-picker-results {
      max-height: 360px;
      overflow-y: auto;
    }
    .dev-picker-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 7px 12px;
      cursor: pointer;
      border-bottom: 1px solid #222;
    }
    .dev-picker-item:hover { background: #2a2a4a; }
    .dev-picker-item.selected { background: #1a3a5c; }
    .dev-item-name { color: #fff; font-weight: 600; min-width: 140px; }
    .dev-item-company { color: #4fc3f7; flex: 1; }
    .dev-item-meta {
      color: #888;
      font-size: 10px;
      display: flex;
      align-items: center;
      gap: 6px;
      margin-left: auto;
    }
    .dev-badge {
      padding: 1px 5px;
      border-radius: 3px;
      font-size: 9px;
      font-weight: 700;
    }
    .dev-badge.admin { background: #e74c3c; color: #fff; }
    .dev-badge.suppress { background: #f39c12; color: #000; }
    .dev-badge.type { background: #1a5276; color: #85c1e9; }
    .dev-badge.group { background: #1e4620; color: #82e085; }
    .dev-picker-empty { padding: 16px; color: #666; text-align: center; }
    .dev-picker-context {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-left: auto;
    }
    .dev-ctx-tag {
      background: #2a3a4a;
      color: #8bc9e8;
      padding: 2px 6px;
      border-radius: 3px;
      font-size: 10px;
    }
    .dev-ctx-perms { color: #888; font-size: 10px; }
    .dev-shelf-status {
      display: flex; align-items: center; gap: 8px; width: 100%;
      padding: 2px 0 0 0; font-size: 10px; color: #888;
    }
    .dev-shelf-app { white-space: nowrap; }
    .dev-shelf-all { color: #4caf50; font-weight: 700; margin-left: 2px; }
    .dev-shelf-shelf { color: #ff9800; font-weight: 700; margin-left: 2px; }
    .dev-shelf-d_shelf { color: #42a5f5; font-weight: 700; margin-left: 2px; }
    .dev-shelf-0 { color: #ef5350; font-weight: 700; margin-left: 2px; }
  `]
})
export class DevUserPickerComponent {
  devUser = inject(DevUserService);
  sessionCtx = inject(SessionContextService);
  private api = inject(AdminApiService);

  readonly shelfAppTypes = ['VA', 'FA', 'IA', 'RILA'];

  shelfLabel(access: string): string {
    switch (access) {
      case 'all': return 'All';
      case 'shelf': return 'Shelf';
      case 'd_shelf': return 'D.Shelf';
      case '0': return 'None';
      default: return access;
    }
  }

  hasShelfAccess(): boolean {
    const ctx = this.sessionCtx.context();
    return !!ctx && Object.keys(ctx.shelfAccess).length > 0;
  }

  step = signal<PickerStep>('closed');

  // Company search
  companyResults = signal<any[]>([]);
  companySearchTerm = signal('');

  // User list for selected company
  selectedCompanyId = 0;
  selectedCompanyName = signal('');
  companyUsers = signal<DevUser[]>([]);
  filteredUsers = signal<DevUser[]>([]);
  usersLoading = signal(false);
  private userFilterTerm = '';

  openDropdown() {
    if (this.step() !== 'closed') {
      this.step.set('closed');
    } else {
      this.step.set('companies');
      this.companyResults.set([]);
      this.companySearchTerm.set('');
    }
  }

  clearSelection(event: Event) {
    event.stopPropagation();
    this.devUser.clearUser();
    this.step.set('closed');
  }

  // ── Step 1: Company search ──

  onCompanySearch(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.companySearchTerm.set(value);
    if (value.length < 2) {
      this.companyResults.set([]);
      return;
    }
    this.api.getCompanies(value, 30).subscribe(companies => {
      this.companyResults.set(companies);
    });
  }

  selectCompany(company: any) {
    this.selectedCompanyId = company.id;
    this.selectedCompanyName.set(company.name);
    this.userFilterTerm = '';
    this.usersLoading.set(true);
    this.step.set('users');

    this.api.getCompanyUsers(company.id).subscribe({
      next: (res: any) => {
        const users: any[] = Array.isArray(res) ? res : (res?.users ?? []);
        const mapped = users.map((u: any) => ({
          id: u.id,
          username: u.username ?? u.user_id ?? '',
          first_name: u.first_name ?? '',
          last_name: u.last_name ?? '',
          email: u.email ?? '',
          user_company_id: company.id,
          company_name: company.name,
          user_type_id: u.user_type_id ?? u.user_type ?? 0,
          user_group: u.user_group ?? 0,
          admin: u.admin ?? 0,
          is_inactive: u.is_inactive ?? 0,
          suppress_admin: isAdminSuppressed(u.suppress_admin),
        })) as DevUser[];
        this.companyUsers.set(mapped);
        this.filteredUsers.set(mapped);
        this.usersLoading.set(false);
      },
      error: () => {
        this.companyUsers.set([]);
        this.filteredUsers.set([]);
        this.usersLoading.set(false);
      },
    });
  }

  backToCompanies() {
    this.step.set('companies');
  }

  // ── Step 2: Filter users within company ──

  onUserFilter(event: Event) {
    this.userFilterTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.applyUserFilter();
  }

  private applyUserFilter() {
    const term = this.userFilterTerm;
    if (!term) {
      this.filteredUsers.set(this.companyUsers());
      return;
    }
    this.filteredUsers.set(
      this.companyUsers().filter(u =>
        `${u.first_name} ${u.last_name} ${u.username} ${u.email}`.toLowerCase().includes(term)
      )
    );
  }

  // ── Pick user ──

  pickUser(user: DevUser) {
    this.devUser.selectUser(user);
    this.step.set('closed');
  }

  // ── Label helpers ──

  userTypeLabel(typeId: string | number): string {
    return USER_TYPE_MAP[typeId] ?? `Type ${typeId}`;
  }

  userGroupLabel(groupId: number): string {
    return USER_GROUP_MAP[groupId] ?? `Group ${groupId}`;
  }
}
